import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { StatisticsService } from '../../../core/services/api-services/statistics.service';
import { BreadcrumbComponent, BreadcrumbItem } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { FilterPanelComponent } from "../../../shared/components/filter-panel/filter-panel.component";
import { FilterField, FilterResult } from '../../../shared/components/filter-panel/model/filter-field.model';
import { PageEvent, PaginatorComponent } from "../../../shared/components/paginator/paginator.component";

@Component({
  selector: 'app-cumulative-income',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginatorComponent, BreadcrumbComponent, FilterPanelComponent],
  templateUrl: './cumulative-income.component.html',
  styleUrl: './cumulative-income.component.css'
})
export class CumulativeIncomeComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  dataSubscriber$!: Subscription;

  // ───────────────────────────────────────────────────────────────
  // Dashboard (for the "Cumulative Income" card)
  // ───────────────────────────────────────────────────────────────
  isDashboardLoading = false;
  dashboardError = '';
  dashboard: any = null;

  // ───────────────────────────────────────────────────────────────
  // Group-by-fee (table)
  // ───────────────────────────────────────────────────────────────
  isLoading = false;
  trnFeeError = '';

  fees: any[] = [];
  filteredFees: any[] = [];
  pagedFees: any[] = [];
  searchText = '';

  // pagination (fees)
  feesPage = 1;
  feesPageSize = 10;
  feesPageSizeOptions = [5, 10, 25, 50];

  // pagination (history)
  historyPage = 1;
  historyPageSize = 10;
  historyPageSizeOptions = [5, 10, 25, 50];

  // ───────────────────────────────────────────────────────────────
  // Offcanvas: history for selected fee
  // ───────────────────────────────────────────────────────────────
  isHistoryLoading = false;
  historyError = '';
  selectedFee: any = null;
  history: any[] = [];
  filteredHistory: any[] = [];
  pagedHistory: any[] = [];
  historySearchText: string = '';

  // Date range (YYYY-MM-DD) + cap end date to today
  startDate: string = '';
  endDate: string = '';
  todayYMD: string = ''; // e.g. "2025-09-17"

  // Fees table
  totalFees: number = 0;
  currentFeesPage: number = 1;

  // History (offcanvas)
  totalHistory: number = 0;
  currentHistoryPage: number = 1;

  selectedTrnFeCode: string = '';

  trnFeeCode: string = '';

  companyId: string = '';

  // =============== Server filter Props =============== //

  feeFilterFields: FilterField[] = [
    {
      type: 'radio-group',
      controlName: 'filterType',
      label: 'Filter By:',
      defaultValue: 'CompanyId',
      options: [
        {
          value: 'CompanyId',
          label: 'Company ID',
          field: {
            type: 'text',
            controlName: 'CompanyId',            // key sent to backend
            placeholder: 'Enter Company ID',
            hint: 'Example: 9412265199 (10 digits)',
            validators: [Validators.required, Validators.pattern(/^\d{10}$/), Validators.minLength(10), Validators.maxLength(10)],
          },
        }
      ],
    },
  ];

  // =================================================== //

  constructor(
    private statisticsService: StatisticsService,
    private breadcrumbService: BreadcrumbService,
    private toaster: ToastrService
  ) {
    this.dataSubscriber$ = new Subscription();
  }

  ngOnInit(): void {
    // Subscribe to breadcrumb changes
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => {
        this.breadcrumbItems = items;
      });

    // Set breadcrumbs for this page
    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Treasury', icon: 'bi bi-bank' },
      { label: 'Cumulative Income', icon: 'bi bi-graph-up-arrow' }
    ]);

    this.todayYMD = this.formatYMD(new Date());

    this.fetchDashboardStats();
    this.fetchCumulativeIncomeGroupByFee(this.currentFeesPage, this.feesPageSize, this.trnFeeCode);
  }

  // ───────────────────────────────────────────────────────────────
  // 1) GET /statistics/dashboard
  // ───────────────────────────────────────────────────────────────
  fetchDashboardStats(): void {
    this.isDashboardLoading = true;
    this.dashboardError = '';

    this.dataSubscriber$.add(
      this.statisticsService.getDashboardStats().subscribe({
        next: (response: any) => {
          if (response?.success) {
            this.dashboard = response.data ?? null;
          } else {
            this.dashboardError = response?.message || 'Failed to load dashboard stats.';
          }
          this.isDashboardLoading = false;
        },
        error: (err: any) => {
          this.dashboardError = 'API error (dashboard): ' + (err?.message ?? err);
          this.isDashboardLoading = false;
        }
      })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // 2) GET /statistics/cumulative-income/group-by-fee
  // ───────────────────────────────────────────────────────────────

  fetchCumulativeIncomeGroupByFee(page: number, pageSize: number, trnFeeCode?: string): void {
    this.isLoading = true;
    this.dataSubscriber$.add(
      this.statisticsService.getCumulativeIncomeGroupByFeePaginated(page, pageSize, trnFeeCode || null)
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.fees = response.data.map(x => ({
                ...x,
                trnFeeCode: this.clean(x.trnFeeCode),
                trnFeeDesc: this.clean(x.trnFeeDesc)
              }));
              this.filteredFees = [...this.fees];
              this.pagedFees = [...this.fees]; // server already paged
              this.totalFees = response.totalCount;
              this.currentFeesPage = response.pageNumber;
              this.feesPageSize = response.pageSize;
            } else {
              this.pagedFees = [];
              this.trnFeeError = 'Failed to load fee groups.';
            }
            this.isLoading = false;
          },
          error: (err) => {
            this.pagedFees = [];
            this.trnFeeError = 'API error (group-by-fee): ' + (err?.error?.message ?? err);
            this.isLoading = false;
          }
        })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // 3) GET /statistics/cumulative-income/{code}/history
  // ───────────────────────────────────────────────────────────────

  fetchCumulativeIncomeHistory(trnFeeCode: string, page: number, pageSize: number, companyId?: string): void {
    this.isHistoryLoading = true;
    this.selectedTrnFeCode = trnFeeCode;
    this.historyError = '';
    this.dataSubscriber$.add(
      this.statisticsService.getCumulativeIncomeHistoryPaginated(
        trnFeeCode, page, pageSize, companyId || null,
        this.startDate || null, this.endDate || null
      ).subscribe({
        next: (response) => {
          if (response.success) {
            this.history = response.data.sort((a: any, b: any) => {
              const da = new Date(a.createdDate).getTime();
              const db = new Date(b.createdDate).getTime();
              return db - da;
            });
            this.filteredHistory = [...this.history];
            this.pagedHistory = [...this.history];
            this.totalHistory = response.totalCount;
            this.currentHistoryPage = response.pageNumber;
            this.historyPageSize = response.pageSize;
          } else {
            this.history = [];             
            this.filteredHistory = [];
            this.pagedHistory = [];
            this.totalHistory = 0; 
            this.historyError = 'No history found.';
          }
          this.isHistoryLoading = false;
        },
        error: (err) => {
          this.history = [];        
          this.filteredHistory = []; 
          this.pagedHistory = [];
          this.totalHistory = 0; 
          this.historyError = (err?.error?.message ?? err);
          this.isHistoryLoading = false; 
        }
      })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // UI actions
  // ───────────────────────────────────────────────────────────────
  performSearch(): void {
    const q = (this.searchText || '').toLowerCase().trim();
    if (!q) {
      this.filteredFees = [...this.fees];
      this.pagedFees = [...this.fees];
      return;
    }
    this.filteredFees = this.fees.filter(f =>
      this.toStr(f.trnFeeCode).toLowerCase().includes(q) ||
      this.toStr(f.trnFeeDesc).toLowerCase().includes(q)
    );
    this.pagedFees = [...this.filteredFees];
  }

  // Open offcanvas: set default dates (last 7 days capped to today) and fetch
  openViewFee(fee: any): void {
    if (!fee || (fee.total ?? 0) === 0) return; // prevent opening for zero totals

    this.selectedFee = fee;

    // const today = new Date();
    // const start = new Date(today);
    // start.setDate(today.getDate() - 7);
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);

    this.startDate = this.formatYMD(start);
    this.endDate = this.formatYMD(today);

    this.historySearchText = '';
    this.filteredHistory = [];
    this.resetHistoryPagination?.();
    this.fetchCumulativeIncomeHistory(fee?.trnFeeCode || '', this.currentHistoryPage, this.historyPageSize, this.companyId);
  }


  // Filter history by company name or ID (offcanvas search)
  performHistorySearch(): void {
    const q = (this.historySearchText || '').trim().toLowerCase();
    if (!q) {
      this.filteredHistory = [...this.history];
      this.resetHistoryPagination();
      return;
    }

    this.filteredHistory = this.history.filter(r => {
      const name = (r.companyName ?? '').toString().toLowerCase();
      const id = (r.companyId ?? '').toString().toLowerCase();
      return name.includes(q) || id.includes(q);
    });
    this.resetHistoryPagination();
  }

  // Called by (change) on both date inputs
  onDateRangeChange(): void {
    if (!this.selectedFee) return;

    // clamp endDate to today if user typed a future date
    if (this.endDate) {
      const end = new Date(this.endDate);
      const today = new Date(this.todayYMD);
      if (end > today) {
        this.endDate = this.todayYMD;
      }
    }

    if (this.startDate && this.endDate) {
      const s = new Date(this.startDate);
      const e = new Date(this.endDate);

      if (isNaN(s.getTime()) || isNaN(e.getTime())) {
        this.historyError = 'Invalid date selection.';
        this.history = [];
        this.performHistorySearch();
        return;
      }
      if (s > e) {
        this.historyError = 'Start date cannot be after end date.';
        this.history = [];
        this.performHistorySearch();
        return;
      }
    }

    this.historyError = '';
    this.fetchCumulativeIncomeHistory(this.selectedFee.trnFeeCode, this.currentHistoryPage, this.historyPageSize, this.companyId);
  }

  // ───────────────────────────────────────────────────────────────
  // Pagination: fees table
  // ───────────────────────────────────────────────────────────────
  private resetFeesPagination(): void {
    this.feesPage = 1;
    this.updateFeesPage();
  }

  private updateFeesPage(): void {
    if (this.feesPage < 1) this.feesPage = 1;
    const start = (this.feesPage - 1) * this.feesPageSize;
    this.pagedFees = this.filteredFees.slice(start, start + this.feesPageSize);
  }

  // onCumulativeIncomePageChange(paginatedData: any[]): void {
  //   this.pagedFees = paginatedData;
  // }

  onCumulativeIncomePageChange(event: PageEvent): void {
    this.currentFeesPage = event.page;
    this.feesPageSize = event.pageSize;
    this.fetchCumulativeIncomeGroupByFee(event.page, event.pageSize);
  }

  // ───────────────────────────────────────────────────────────────
  // Pagination: history table (offcanvas)
  // ───────────────────────────────────────────────────────────────
  private resetHistoryPagination(): void {
    this.historyPage = 1;
    this.updateHistoryPage();
  }

  private updateHistoryPage(): void {
    if (this.historyPage < 1) this.historyPage = 1;
    const start = (this.historyPage - 1) * this.historyPageSize;
    this.pagedHistory = this.filteredHistory.slice(start, start + this.historyPageSize);
  }

  // onHistoryPageChange(paginatedData: any[]): void {
  //   this.pagedHistory = paginatedData;
  // }
  onHistoryPageChange(event: PageEvent): void {
    this.currentHistoryPage = event.page;
    this.historyPageSize = event.pageSize;
    this.fetchCumulativeIncomeHistory(this.selectedFee.trnFeeCode, event.page, event.pageSize, this.companyId);
  }

  // ───────────────────────────────────────────────────────────────
  // Helpers
  // ───────────────────────────────────────────────────────────────
  private toStr(v: any): string {
    return v === null || v === undefined ? '' : String(v);
  }

  private clean(v: any): string {
    return this.toStr(v).replace(/\s+/g, ' ').trim();
  }

  private pad2(n: number): string {
    return n < 10 ? '0' + n : '' + n;
  }

  private formatYMD(d: Date): string {
    return `${d.getFullYear()}-${this.pad2(d.getMonth() + 1)}-${this.pad2(d.getDate())}`;
  }

  formatDateTime(date?: string | Date): string {
    if (!date) return '';

    const d = new Date(date);

    const yyyy = d.getFullYear();
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // convert 0 → 12

    return `${yyyy}-${MM}-${dd}, ${hours}:${minutes}:${seconds} ${ampm}`;
  }

  // =============================================================================================== //
  // Server Side Filtration
  // =============================================================================================== //

  onFeeFilter(result: FilterResult): void {

    const filterValue = result['CompanyId']?.toString().trim();
    this.companyId = filterValue || '';

    if (!filterValue) {
      this.toaster.warning('Please enter a value to filter', 'Filter');
      return;
    }

    this.currentHistoryPage = 1;
    this.searchText = '';

    this.fetchCumulativeIncomeHistory(this.selectedTrnFeCode, this.currentHistoryPage, this.historyPageSize, filterValue);
  }

  onFeeClear(): void {
    this.currentHistoryPage = 1;
    this.searchText = '';
    this.companyId = '';
    this.historyError = '';
    this.fetchCumulativeIncomeHistory(this.selectedTrnFeCode, this.currentHistoryPage, this.historyPageSize);
  }

  closeOffCanvas(): void {
    this.selectedFee = null;
    this.history = [];
    this.filteredHistory = [];
    this.historyError = '';
    this.historySearchText = '';
    this.selectedTrnFeCode = '';
    this.resetHistoryPagination();
  }

  ngOnDestroy(): void {
    this.dataSubscriber$.unsubscribe();
  }
}
